import { useState } from 'react'
import { useTheme } from '../../theme'
import { avecAlpha } from '../../lib/couleurs'
import { CONTACT_EMAIL, WHATSAPP } from '../../content/contact'

/**
 * Nous contacter (prototype v7.4).
 *
 * Deux canaux qui repondent VRAIMENT, puis un formulaire.
 *
 * ECART ASSUME AVEC LE PROTOTYPE : celui-ci affiche un numero de ticket
 * (« SUP-2026-0142 ») et promet un fil de suivi. Il n'existe pas de backend de
 * support — inventer un numero de ticket serait promettre un suivi qu'on ne
 * peut pas tenir, exactement ce que la v7.4 reproche a une RINA qui improvise.
 * Le formulaire compose donc un **e-mail pre-rempli** (sujet + message + la
 * casquette active) : le fil existe, c'est celui de la boite mail, et la reponse
 * arrive vraiment. A rebrancher sur un vrai suivi le jour ou il existera
 * (cf. docs/BACKLOG-COMPTE.md).
 *
 * L'« urgence le jour du marche » du prototype n'est pas reprise : elle suppose
 * un numero de coordinateur du jour qu'on n'a pas. On n'invente pas un numero
 * d'urgence.
 */

// Numero deja utilise par le site public Manrina (cf. CHANGELOG v0.4x) —
// constantes partagees avec le dossier exposant depuis S73.

const SUJETS = ['Ma commande', 'Paiement & wallet', 'Un produit, un producteur', 'Mon compte', 'Côté producteur', 'Autre']

function AideContactPage({ tc, ts, cardBg, hubMode, user }) {
  const { isWeb3 } = useTheme()
  const [ouvert, setOuvert] = useState(false)
  const [sujet, setSujet] = useState(null)
  const [message, setMessage] = useState('')

  const casquette = hubMode === 'producer' ? 'producteur' : 'client'
  const pret = !!sujet && message.trim().length >= 5

  const lienMail = () => {
    const corps = [
      message.trim(),
      '',
      '—',
      `Casquette : ${casquette}`,
      user?.prenom || user?.nom ? `Compte : ${[user?.prenom, user?.nom].filter(Boolean).join(' ')}` : null,
    ].filter((l) => l !== null).join('\n')
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`[${sujet}] Demande depuis Lyannaj`)}&body=${encodeURIComponent(corps)}`
  }

  const canaux = [
    {
      e: '💬', t: 'WhatsApp Manrina', s: 'En général sous 2h en journée',
      tag: 'Le plus rapide', c: '#25D366',
      href: `https://wa.me/${WHATSAPP}`,
    },
    {
      e: '✉️', t: CONTACT_EMAIL, s: 'Réponse sous 24 à 48h ouvrées',
      c: 'rgb(var(--c-teal))',
      href: `mailto:${CONTACT_EMAIL}`,
    },
  ]

  return (
    <>
      <div className="flex flex-col gap-2.5">
        {canaux.map((k) => (
          <a key={k.t} href={k.href} target="_blank" rel="noreferrer"
            className={`w-full flex items-center gap-3 px-4 py-[15px] rounded-[22px] border animate-fade-up active:scale-[0.99] transition-transform ${cardBg}`}>
            <span className="w-[42px] h-[42px] rounded-[14px] flex-shrink-0 flex items-center justify-center text-xl"
              style={{ background: avecAlpha(k.c, 0.12), border: '1px solid rgba(255,255,255,0.7)' }}>{k.e}</span>
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-2">
                <span className={`text-sm font-bold truncate ${tc}`}>{k.t}</span>
                {k.tag && (
                  <span className="text-[9.5px] font-extrabold rounded-full px-2 py-[3px] flex-shrink-0"
                    style={{ color: '#0d6b5f', background: 'rgba(20,184,166,0.16)' }}>{k.tag}</span>
                )}
              </span>
              <span className={`block text-[11.5px] mt-0.5 ${ts}`}>{k.s}</span>
            </span>
            <span className={`text-sm ${ts}`}>›</span>
          </a>
        ))}
      </div>

      <p className="text-[11px] font-bold uppercase tracking-[0.16em] mx-0.5 mb-2.5 mt-6 text-teal">
        Écrire à l’équipe
      </p>

      {!ouvert ? (
        <button onClick={() => setOuvert(true)}
          className={`w-full flex items-center gap-3 px-4 py-[15px] rounded-[22px] border text-left active:scale-[0.99] transition-transform animate-fade-up ${cardBg}`}>
          <span className="w-[42px] h-[42px] rounded-[14px] flex-shrink-0 flex items-center justify-center text-xl"
            style={{ background: 'rgba(244,137,83,0.16)', border: '1px solid rgba(255,255,255,0.7)' }}>📝</span>
          <span className="flex-1 min-w-0">
            <span className={`block text-sm font-bold ${tc}`}>Ouvrir le formulaire</span>
            <span className={`block text-[11.5px] mt-0.5 ${ts}`}>On prépare votre message, vous n’avez qu’à l’envoyer</span>
          </span>
          <span className={`text-sm ${ts}`}>›</span>
        </button>
      ) : (
        <div className={`rounded-[22px] border px-4 py-4 animate-fade-up ${cardBg}`}>
          <p className={`text-[11.5px] font-extrabold mb-2 ${ts}`}>De quoi s’agit-il ?</p>
          <div className="flex flex-wrap gap-[7px]">
            {SUJETS.map((s) => {
              const on = sujet === s
              return (
                <button key={s} onClick={() => setSujet(s)}
                  className={`px-3 py-2 rounded-full text-xs font-bold border ${on ? 'text-creme border-transparent' : tc}`}
                  style={on
                    ? { background: 'rgb(var(--c-green))' }
                    : { background: isWeb3 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.65)', borderColor: 'rgba(7,62,61,0.14)' }}>
                  {s}
                </button>
              )
            })}
          </div>

          <label htmlFor="contact-msg" className={`block text-[11.5px] font-extrabold mt-4 mb-2 ${ts}`}>
            Votre message
          </label>
          <textarea id="contact-msg" rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Dites-nous ce qui se passe — le plus précis possible nous fait gagner un aller-retour."
            className={`w-full rounded-2xl border px-3 py-3 text-[13px] leading-relaxed outline-none resize-y ${tc}`}
            style={{ background: isWeb3 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.65)', borderColor: 'rgba(7,62,61,0.14)' }} />

          <p className={`text-[10.5px] leading-relaxed mt-3 mx-0.5 ${ts}`}>
            On joint automatiquement votre casquette ({casquette}) — ça nous évite de vous la demander.
          </p>

          {pret ? (
            <a href={lienMail()}
              className="mt-3 block w-full text-center py-3.5 rounded-2xl text-white text-sm font-extrabold"
              style={{ background: 'linear-gradient(135deg, rgb(var(--c-primary)), #e8703a)' }}>
              Envoyer
            </a>
          ) : (
            <span className="mt-3 block w-full text-center py-3.5 rounded-2xl text-sm font-extrabold"
              style={{ background: 'rgba(7,62,61,0.15)', color: 'rgba(7,62,61,0.45)' }}>
              Envoyer
            </span>
          )}
          <button onClick={() => setOuvert(false)}
            className={`mt-2 w-full py-2.5 rounded-2xl text-[12.5px] font-bold ${ts}`}>
            Annuler
          </button>
        </div>
      )}
    </>
  )
}

export default AideContactPage
